import { FC, KeyboardEvent } from "react";
import { Box } from "@mui/material";
import { Role } from "src/domains/myPageContent/userManage/userManage-query/usersOfProject.modal";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { CAPTION, HOVER_TRANSITION_MS, REDUCED_MOTION_SX } from "src/domains/shared/components/StateTransition";
import { MYPAGE_TOGGLE_TRACK_BG } from "../myPageStyles";

interface RoleToggleProps {
  ariaLabel: string;
  value: Role;
  onChange: (role: Role) => void;
}

const ROLES: Role[] = ["inspector", "viewer"];
const SEGMENT_WIDTH_PX = 72;
const SEGMENT_GAP_PX = 2;

/** 권한 2칸 토글 — 라디오 그룹으로 읽히고, 선택 표시는 180ms 동안 미끄러져 옮겨 간다. */
export const RoleToggle: FC<RoleToggleProps> = ({ ariaLabel, value, onChange }) => {
  const selectedIndex = ROLES.indexOf(value);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const next = ROLES[(selectedIndex + 1) % ROLES.length];
    onChange(next);
    const group = event.currentTarget.closest('[role="radiogroup"]');
    group?.querySelector<HTMLElement>(`[data-role="${next}"]`)?.focus();
  };

  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      sx={{
        position: "relative",
        display: "inline-flex",
        flexShrink: 0,
        gap: `${SEGMENT_GAP_PX}px`,
        padding: "2px",
        borderRadius: "8px",
        backgroundColor: MYPAGE_TOGGLE_TRACK_BG,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: 2,
          left: 2,
          width: SEGMENT_WIDTH_PX,
          height: 24,
          borderRadius: "6px",
          backgroundColor: COLOR_CONSTANTS.common.white_states.main,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
          transform: `translateX(${selectedIndex * (SEGMENT_WIDTH_PX + SEGMENT_GAP_PX)}px)`,
          transition: `transform ${HOVER_TRANSITION_MS}ms ease-out`,
          ...REDUCED_MOTION_SX,
        }}
      />
      {ROLES.map((role) => {
        const checked = role === value;
        return (
          <Box
            key={role}
            component="button"
            type="button"
            role="radio"
            aria-checked={checked}
            data-role={role}
            tabIndex={checked ? 0 : -1}
            onClick={() => !checked && onChange(role)}
            onKeyDown={handleKeyDown}
            sx={{
              position: "relative",
              width: SEGMENT_WIDTH_PX,
              height: 24,
              border: 0,
              borderRadius: "6px",
              background: "transparent",
              fontFamily: "inherit",
              fontSize: 11,
              fontWeight: checked ? 600 : 500,
              color: checked ? COLOR_CONSTANTS.primary.main : CAPTION,
              cursor: checked ? "default" : "pointer",
              transition: `color ${HOVER_TRANSITION_MS}ms ease-out`,
              "&:focus-visible": { outline: "none", boxShadow: `0 0 0 2px ${COLOR_CONSTANTS.primary._states.focusVisible}` },
              ...REDUCED_MOTION_SX,
            }}
          >
            {role}
          </Box>
        );
      })}
    </Box>
  );
};
